import type { PublishedCard } from "../../types";
import { parseSealDeadline } from "../../utils/useSealTimeGate";

export type DeckCardTocExp = {
  id?: number;
  title?: string;
  release_date?: string;
  status?: string;
  start_time?: string;
  session_type?: string;
  theme_title?: string;
  type?: string;
};

export type DeckCardTocItem = {
  title?: string;
  type?: string;
  description?: string;
  exps?: DeckCardTocExp[];
};

/** Merge API TOC + root exps (kept here to avoid circular import with deckCardTocDisplay). */
export const mergeDeckCardToc = (
  tableOfContents: DeckCardTocItem[] | undefined | null,
  exps: DeckCardTocExp[] | undefined | null,
  cardType?: string
): DeckCardTocItem[] => {
  const toc = Array.isArray(tableOfContents) ? [...tableOfContents] : [];
  const rootExps = Array.isArray(exps) ? exps.filter(Boolean) : [];
  if (!rootExps.length) return toc;
  if (toc.some((item) => Array.isArray(item.exps) && item.exps.length > 0)) return toc;
  return [
    ...toc,
    {
      type: cardType || "Demo_Card",
      title: undefined,
      description: undefined,
      exps: rootExps,
    },
  ];
};

export type DeckCardSummaryActivity = {
  type?: string;
  text?: string;
  username?: string;
  created_at?: string;
};

/** UI summary for expand back-face; derived from engagement fields on the record. */
export type DeckCardThumbnailSummary = {
  clicked_count?: number;
  viewed_count?: number;
  total_reactions?: number;
  total_comments?: number;
  total_gifts?: number;
  recent_reactions?: DeckCardSummaryActivity[];
  recent_comments?: DeckCardSummaryActivity[];
  recent_talent_replies?: DeckCardSummaryActivity[];
};

export type DeckCardThumbnailContent = {
  id?: number;
  talent_id?: number;
  title?: string;
  description?: string;
  thumbnail?: string;
  price?: number;
  status?: string;
  created_at?: string;
  updated_at?: string;
  intro_blocks?: unknown;
  settings?: unknown;
  [key: string]: unknown;
};

export type DeckCardThumbnailTalent = {
  id?: number;
  username?: string;
  profile_image?: string;
  view_name?: string;
  bio?: string;
};

/** Matches `/main-card/card-thumbnail` list/detail items (after TOC merge at load). */
export type DeckCardThumbnailRecord = {
  id: number;
  clicked_count?: number;
  viewed_count?: number;
  collector_count?: number;
  talent_id?: number;
  template?: string;
  exclusive_template?: string | null;
  cover_image_url: string;
  ending_image_url?: string | null;
  seal_time?: string | null;
  price?: number;
  /** API camelCase — passed through as-is (do not normalize). */
  limited_edition?: number | null;
  status?: string;
  deck_status?: boolean;
  card_status?: string;
  type?: string;
  ref_id?: number;
  user_comment_count?: number;
  user_reaction_count?: number;
  user_tip_count?: number;
  created_at?: string;
  updated_at?: string;
  talent?: DeckCardThumbnailTalent;
  purchase?: unknown | null;
  publish_to?: string[];
  board?: unknown | null;
  partner_id?: number;
  partner?: {
    id?: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    profile_image?: string | null;
  } | null;
  recent_user_comment?: unknown | null;
  recent_talent_comment?: unknown | null;
  recent_user_reaction?: unknown | null;
  content?: DeckCardThumbnailContent | null;
  /** True when this thumbnail is a local-partner invite card. */
  partner_card?: boolean;
  /** Merged at load from API `table_of_contents` + top-level `exps`. */
  table_of_contents?: DeckCardTocItem[];
};

export type DeckCardDisplayData = {
  coverImageUrl: string;
  thumbnailUrl: string;
  imageUrl: string;
  purchase: {
    id?: number | string | null;
  };
  type: string;
  typeLabel: string;
  title: string;
  description: string;
  price?: number;
  talent?: DeckCardThumbnailTalent;
};

const toNumber = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const toNonNegativeNumber = (value: unknown): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};

export const formatDeckCardTypeLabel = (type?: string) => {
  if (!type) return "Card";

  // return type
  //   .replace(/_card$/i, "")
  //   .replace(/\_/g, " ")
  //   .replace(/\b\w/g, (char) => char.toUpperCase())
  //   .trim();
  const formattedType = type.replaceAll("_", " ").toUpperCase();

  return formattedType === "SINGLE CARD" ? "SINGLE ALBUM CARD" : formattedType;
};

export const getCardThumbnailPurchaseId = (
  record?: DeckCardThumbnailRecord | null
): number | undefined => {
  const purchase = record?.purchase;
  if (purchase == null || typeof purchase !== "object") return undefined;
  const id = Number((purchase as { id?: unknown }).id);
  return Number.isFinite(id) && id > 0 ? id : undefined;
};

export const isCardThumbnailPurchased = (record?: DeckCardThumbnailRecord | null): boolean =>
  getCardThumbnailPurchaseId(record) != null;

const normalizeTocExps = (value: unknown): DeckCardTocExp[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const row = (item ?? {}) as Record<string, unknown>;
    return {
      id: toNumber(row.id) ?? undefined,
      title:
        (typeof row.title === "string" && row.title) ||
        (typeof row.theme_title === "string" && row.theme_title) ||
        undefined,
      release_date: typeof row.release_date === "string" ? row.release_date : undefined,
      status: typeof row.status === "string" ? row.status : undefined,
      start_time: typeof row.start_time === "string" ? row.start_time : undefined,
      session_type: typeof row.session_type === "string" ? row.session_type : undefined,
      theme_title: typeof row.theme_title === "string" ? row.theme_title : undefined,
      type: typeof row.type === "string" ? row.type : undefined,
    };
  });
};

const normalizeToc = (value: unknown): DeckCardTocItem[] => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => {
    const row = (item ?? {}) as Record<string, unknown>;
    return {
      title: typeof row.title === "string" ? row.title : undefined,
      type: typeof row.type === "string" ? row.type : undefined,
      description: typeof row.description === "string" ? row.description : undefined,
      exps: normalizeTocExps(row.exps),
    };
  });
};

const stripHtml = (value: string) =>
  value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeRecentUserReaction = (value: unknown): DeckCardSummaryActivity[] => {
  if (!value) return [];
  const items = Array.isArray(value) ? value : [value];
  return items.flatMap((item): DeckCardSummaryActivity[] => {
    if (!item || typeof item !== "object") return [];
    const row = item as Record<string, unknown>;
    const user = (row.user ?? {}) as Record<string, unknown>;
    return [
      {
        type: typeof row.type === "string" ? row.type : undefined,
        text:
          (typeof row.text === "string" && row.text) ||
          (typeof row.message === "string" && row.message) ||
          undefined,
        username:
          (typeof row.username === "string" && row.username) ||
          (typeof user.username === "string" && user.username) ||
          undefined,
        created_at: typeof row.created_at === "string" ? row.created_at : undefined,
      },
    ];
  });
};

const normalizeRecentUserComment = (
  value: unknown,
  talent?: DeckCardThumbnailTalent
): {
  recent_comments: DeckCardSummaryActivity[];
  recent_talent_replies: DeckCardSummaryActivity[];
} => {
  if (!value || typeof value !== "object") {
    return { recent_comments: [], recent_talent_replies: [] };
  }
  const row = value as Record<string, unknown>;
  const user = (row.user ?? {}) as Record<string, unknown>;
  const content = typeof row.content === "string" ? stripHtml(row.content) : undefined;
  const giftPrice = toNonNegativeNumber(row.gift_price);
  const hasGift = giftPrice != null && giftPrice > 0;
  const recent_comments: DeckCardSummaryActivity[] = content
    ? [
        {
          type: hasGift ? "gift" : "comment",
          text: hasGift ? `${content} · Gift $${giftPrice}` : content,
          username:
            (typeof user.username === "string" && user.username) ||
            (typeof row.username === "string" && row.username) ||
            undefined,
          created_at: typeof row.created_at === "string" ? row.created_at : undefined,
        },
      ]
    : [];

  const recent_talent_replies: DeckCardSummaryActivity[] = Array.isArray(row.replies)
    ? row.replies.flatMap((reply): DeckCardSummaryActivity[] => {
        if (!reply || typeof reply !== "object") return [];
        const r = reply as Record<string, unknown>;
        const replyUser = (r.user ?? {}) as Record<string, unknown>;
        const replyContent = typeof r.content === "string" ? stripHtml(r.content) : undefined;
        if (!replyContent) return [];
        const isTalentReply = Boolean(r.talent_id);
        return [
          {
            type: "reply",
            text: replyContent,
            username: isTalentReply
              ? talent?.username || "Talent"
              : (typeof replyUser.username === "string" && replyUser.username) ||
                (typeof r.username === "string" && r.username) ||
                undefined,
            created_at: typeof r.created_at === "string" ? r.created_at : undefined,
          },
        ];
      })
    : [];

  return { recent_comments, recent_talent_replies };
};

const normalizeTalent = (value: unknown): DeckCardThumbnailTalent | undefined => {
  if (!value || typeof value !== "object") return undefined;
  const row = value as Record<string, unknown>;
  return {
    id: toNumber(row.id) ?? undefined,
    username: typeof row.username === "string" ? row.username : undefined,
    profile_image: typeof row.profile_image === "string" ? row.profile_image : undefined,
    view_name: typeof row.view_name === "string" ? row.view_name : undefined,
    bio: typeof row.bio === "string" ? row.bio : undefined,
  };
};

const normalizeContent = (value: unknown): DeckCardThumbnailContent | null => {
  if (!value || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  return {
    ...row,
    id: toNumber(row.id) ?? undefined,
    talent_id: toNumber(row.talent_id) ?? undefined,
    title: typeof row.title === "string" ? row.title : undefined,
    description: typeof row.description === "string" ? row.description : undefined,
    thumbnail: typeof row.thumbnail === "string" ? row.thumbnail : undefined,
    price: toNonNegativeNumber(row.price),
    status: typeof row.status === "string" ? row.status : undefined,
    created_at: typeof row.created_at === "string" ? row.created_at : undefined,
    updated_at: typeof row.updated_at === "string" ? row.updated_at : undefined,
  };
};

/** Derive expand back-face summary from thumbnail engagement fields. */
export const getDeckCardThumbnailSummary = (
  record?: DeckCardThumbnailRecord | null
): DeckCardThumbnailSummary | null => {
  if (!record) return null;
  const hasCounts =
    record.user_reaction_count != null ||
    record.user_comment_count != null ||
    record.user_tip_count != null ||
    record.clicked_count != null ||
    record.viewed_count != null;
  const hasRecent =
    record.recent_user_reaction != null ||
    record.recent_user_comment != null ||
    record.recent_talent_comment != null;
  if (!hasCounts && !hasRecent) return null;

  const fromUserComment = normalizeRecentUserComment(record.recent_user_comment, record.talent);
  const fromTalentComment = normalizeRecentUserComment(record.recent_talent_comment, record.talent);
  return {
    total_reactions: toNonNegativeNumber(record.user_reaction_count),
    total_comments: toNonNegativeNumber(record.user_comment_count),
    total_gifts: toNonNegativeNumber(record.user_tip_count),
    recent_reactions: normalizeRecentUserReaction(record.recent_user_reaction),
    recent_comments: fromUserComment.recent_comments,
    recent_talent_replies:
      fromTalentComment.recent_comments.length > 0
        ? fromTalentComment.recent_comments
        : fromUserComment.recent_talent_replies,
    clicked_count: toNonNegativeNumber(record.clicked_count),
    viewed_count: toNonNegativeNumber(record.viewed_count),
  };
};

/** Light load of a thumbnail API item; merges TOC + root exps. */
export const parseDeckCardThumbnailRecord = (raw: unknown): DeckCardThumbnailRecord | null => {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const cardId = toNumber(row.id);
  if (!cardId) return null;

  const coverImageUrl = typeof row.cover_image_url === "string" ? row.cover_image_url : "";
  const type = typeof row.type === "string" ? row.type : undefined;
  const content = normalizeContent(row.content);
  const toc = normalizeToc(row.table_of_contents);
  const contentExps = normalizeTocExps(
    content && Array.isArray((content as { exps?: unknown }).exps)
      ? (content as { exps: unknown }).exps
      : undefined
  );
  const rootExps = normalizeTocExps(row.exps);
  const mergedExps = rootExps.length > 0 ? rootExps : contentExps;

  return {
    id: cardId,
    clicked_count: toNonNegativeNumber(row.clicked_count),
    viewed_count: toNonNegativeNumber(row.viewed_count),
    collector_count: toNonNegativeNumber(row.collector_count),
    talent_id: toNumber(row.talent_id) ?? undefined,
    template: typeof row.template === "string" ? row.template : undefined,
    exclusive_template:
      typeof row.exclusive_template === "string" || row.exclusive_template === null
        ? (row.exclusive_template as string | null)
        : undefined,
    cover_image_url: coverImageUrl,
    ending_image_url:
      typeof row.ending_image_url === "string" || row.ending_image_url === null
        ? (row.ending_image_url as string | null)
        : undefined,
    seal_time:
      typeof row.seal_time === "string" || row.seal_time === null
        ? (row.seal_time as string | null)
        : typeof content?.seal_time === "string"
          ? content.seal_time
          : toNonNegativeNumber(row.seal_time) != null
            ? String(row.seal_time)
            : undefined,
    price: toNonNegativeNumber(row.price),
    limited_edition: row.limited_edition as number | null | undefined,
    deck_status:
      typeof row.deck_status === "boolean"
        ? row.deck_status
        : row.deck_status != null
          ? Boolean(row.deck_status)
          : undefined,
    status: typeof row.status === "string" ? row.status : undefined,
    card_status: typeof row.card_status === "string" ? row.card_status : undefined,
    type,
    ref_id: toNumber(row.ref_id) ?? undefined,
    user_comment_count: toNonNegativeNumber(row.user_comment_count),
    user_reaction_count: toNonNegativeNumber(row.user_reaction_count),
    user_tip_count: toNonNegativeNumber(row.user_tip_count),
    created_at: typeof row.created_at === "string" ? row.created_at : undefined,
    updated_at: typeof row.updated_at === "string" ? row.updated_at : undefined,
    talent: normalizeTalent(row.talent),
    purchase: row.purchase ?? null,
    publish_to: Array.isArray(row.publish_to)
      ? row.publish_to.filter((v): v is string => typeof v === "string")
      : undefined,
    board: row.board ?? null,
    partner_id: toNumber(row.partner_id) ?? undefined,
    partner:
      row.partner && typeof row.partner === "object"
        ? (row.partner as DeckCardThumbnailRecord["partner"])
        : null,
    recent_user_comment: row.recent_user_comment ?? null,
    recent_talent_comment: row.recent_talent_comment ?? null,
    recent_user_reaction: row.recent_user_reaction ?? null,
    content,
    partner_card:
      row.partner_card === true ||
      row.partner_card === "true" ||
      row.is_partner_card === true ||
      row.is_partner_card === "true" ||
      content?.partner_card === true ||
      content?.partner_card === "true"
        ? true
        : row.partner_card === false ||
            row.partner_card === "false" ||
            row.is_partner_card === false ||
            content?.partner_card === false
          ? false
          : undefined,
    table_of_contents: mergeDeckCardToc(toc, mergedExps, type),
  };
};

export const parseDeckCardThumbnailList = (response: unknown): DeckCardThumbnailRecord[] => {
  const payload = (response as { data?: unknown })?.data ?? response;
  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { items?: unknown[] })?.items)
      ? (payload as { items: unknown[] }).items
      : [];
  return rows
    .map(parseDeckCardThumbnailRecord)
    .filter((item): item is DeckCardThumbnailRecord => Boolean(item));
};

/** Partner invite cards from `only_partner_card=true`, or rows flagged `partner_card`. */
export const parsePartnerCardThumbnailList = (
  response: unknown
): DeckCardThumbnailRecord[] => {
  const list = parseDeckCardThumbnailList(response);
  const hasFlag = list.some((item) => item.partner_card != null);
  if (!hasFlag) return list;
  return list.filter((item) => item.partner_card === true);
};

export const thumbnailRecordToPublishedCard = (record: DeckCardThumbnailRecord): PublishedCard => {
  const content: Record<string, unknown> = { ...(record.content ?? {}) };
  const title =
    (typeof content.title === "string" && content.title) ||
    record.content?.title ||
    "Untitled card";
  const description =
    (typeof content.description === "string" && content.description) ||
    record.content?.description ||
    "";

  if (!content.title) content.title = title;
  if (!content.description) content.description = description;
  const thumbnailFromContent =
    typeof content.thumbnail === "string" ? content.thumbnail : record.content?.thumbnail;
  if (!thumbnailFromContent && record.cover_image_url) {
    content.thumbnail = record.cover_image_url;
  }

  const purchased = isCardThumbnailPurchased(record);
  content.is_purchased = purchased;

  const pages = [
    {
      id: record.content?.id ?? record.ref_id ?? 0,
      order: 1,
      type: record.type ?? "",
      content,
      assets: [] as number[],
    },
  ];

  return {
    id: String(record.id),
    title: record.talent?.username ? `${record.talent.username}'s Card` : title,
    coverImageUrl: record.cover_image_url ?? "",
    endingImageUrl: record.ending_image_url ?? "",
    template: record.template ?? "",
    price: record.price,
    limited_edition: record.limited_edition,
    ...(record.deck_status != null ? { deck_status: Boolean(record.deck_status) } : {}),
    seal_time: record.seal_time ?? null,
    card_status: record.card_status,
    type: "Custom",
    createdAt: record.created_at ?? "",
    collector_count: record.collector_count,
    clicked_count: record.clicked_count,
    viewed_count: record.viewed_count,
    pages,
    talent: record.talent?.id
      ? {
          id: record.talent.id,
          username: record.talent.username,
          profile_image: record.talent.profile_image,
        }
      : undefined,
    ...(purchased ? { purchase: record.purchase } : {}),
    ...(record.partner_id != null ? { partner_id: record.partner_id } : {}),
    ...(record.partner ? { partner: record.partner } : {}),
    ...(Array.isArray(record.table_of_contents) && record.table_of_contents.length > 0
      ? { table_of_contents: record.table_of_contents }
      : {}),
  };
};

export const getDeckCardDisplayData = (
  record?: DeckCardThumbnailRecord | null,
  fallbackCard?: PublishedCard | null
): DeckCardDisplayData => {
  const content = (record?.content ?? {}) as Record<string, unknown>;
  const fallbackContent = fallbackCard?.pages?.[0]?.content ?? {};

  const type =
    record?.type ||
    (typeof fallbackCard?.pages?.[0]?.type === "string" ? fallbackCard.pages[0].type : "") ||
    "";

  const title =
    (typeof content.title === "string" && content.title) ||
    record?.content?.title ||
    fallbackContent?.title ||
    fallbackCard?.title ||
    "Untitled card";

  const description =
    (typeof content.description === "string" && content.description) ||
    record?.content?.description ||
    fallbackContent?.description ||
    "";

  const coverImageUrl = record?.cover_image_url || fallbackCard?.coverImageUrl || "";
  const thumbnailUrl =
    (typeof content.thumbnail === "string" && content.thumbnail) ||
    record?.content?.thumbnail ||
    coverImageUrl;

  const talent =
    record?.talent ||
    (fallbackCard?.talent
      ? {
          id: fallbackCard.talent.id,
          username: fallbackCard.talent.username,
          profile_image: fallbackCard.talent.profile_image,
        }
      : undefined);

  const priceFromContent =
    content.price != null
      ? Number(content.price)
      : record?.content?.price != null
        ? Number(record.content.price)
        : undefined;

  return {
    coverImageUrl,
    thumbnailUrl,
    imageUrl: thumbnailUrl || coverImageUrl,
    type,
    typeLabel: formatDeckCardTypeLabel(type),
    purchase: record?.purchase ?? { id: null },
    title,
    description,
    price: Number.isFinite(priceFromContent as number)
      ? (priceFromContent as number)
      : record?.price,
    talent,
  };
};

/**
 * Same hero URL as `DeckCardThumbnailTile` (`display.imageUrl`).
 * Sent to Kakao as `image_url` after HTTPS normalization.
 */
export const getDeckCardCoverImageUrl = (
  record?: DeckCardThumbnailRecord | null,
  fallbackCard?: PublishedCard | null
): string => {
  const display = getDeckCardDisplayData(record, fallbackCard);
  return display.imageUrl?.trim() || "";
};

/** Explains which URL each UI surface uses (for Kakao share debugging). */
export type DeckCardThumbnailSourceDebug = {
  mainCardId?: number | string;
  /** `DeckCardThumbnailTile` → `display.imageUrl` (content.thumbnail, then cover). */
  deckCardThumbnailTileImageUrl: string;
  /** `DeckThumbnail` / `DeckBook3D` — talent deck bundle only (`deck.thumbnail`), not per-card share. */
  deckBundleThumbnailNote: string;
  recordCoverImageUrl: string;
  publishedCoverImageUrl: string;
  contentThumbnail: string;
  displayCoverImageUrl: string;
  displayThumbnailUrl: string;
  /** Raw cover passed into Kakao (`getDeckCardCoverImageUrl`). */
  kakaoCoverImageUrlRaw: string;
};

export const getDeckCardThumbnailSourceDebug = (
  record?: DeckCardThumbnailRecord | null,
  fallbackCard?: PublishedCard | null
): DeckCardThumbnailSourceDebug => {
  const content = (record?.content ?? {}) as Record<string, unknown>;
  const display = getDeckCardDisplayData(record, fallbackCard);

  return {
    mainCardId: fallbackCard?.id ?? record?.id,
    deckCardThumbnailTileImageUrl: display.imageUrl?.trim() ?? "",
    deckBundleThumbnailNote:
      "Charm DeckThumbnail uses TalentDeck.thumbnail (bundle cover), not this card record.",
    recordCoverImageUrl: record?.cover_image_url?.trim() ?? "",
    publishedCoverImageUrl: fallbackCard?.coverImageUrl?.trim() ?? "",
    contentThumbnail:
      (typeof content.thumbnail === "string" ? content.thumbnail : "") ||
      record?.content?.thumbnail?.trim() ||
      "",
    displayCoverImageUrl: display.coverImageUrl?.trim() ?? "",
    displayThumbnailUrl: display.thumbnailUrl?.trim() ?? "",
    kakaoCoverImageUrlRaw: getDeckCardCoverImageUrl(record, fallbackCard),
  };
};

export const getTocItemLabel = (item: DeckCardTocItem) =>
  item.title || formatDeckCardTypeLabel(item.type) || "Untitled";

export const getTocItemSecondary = (item: DeckCardTocItem) => {
  const description = item.description
    ? item.description
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim()
    : "";
  if (description) return description;
  if (item.exps?.length) {
    return item.exps
      .map((exp) => exp.title || exp.theme_title)
      .filter(Boolean)
      .join(" · ");
  }
  return item.type ? formatDeckCardTypeLabel(item.type) : undefined;
};

/** Display date for card summary living plan, e.g. "Oct 31, 2026". */
export const formatDeckCardDisplayDate = (value?: string | number | Date | null): string => {
  if (value == null || value === "") return "";

  const ms =
    value instanceof Date
      ? value.getTime()
      : typeof value === "number"
        ? parseSealDeadline(value)
        : (parseSealDeadline(value) ?? new Date(value).getTime());

  if (ms == null || !Number.isFinite(ms)) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(ms));
};

const getDeckCardExpReleaseMs = (exp: DeckCardTocExp): number | null => {
  const raw = exp.release_date || exp.start_time;
  if (!raw) return null;
  const ms = parseSealDeadline(raw) ?? new Date(raw).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const readUnknownExps = (value: unknown): DeckCardTocExp[] => {
  if (!value || typeof value !== "object") return [];
  const row = value as Record<string, unknown>;
  return normalizeTocExps(row.exps);
};

/** Merge TOC + exps from thumbnail list row and expanded card payload. */
export const resolveDeckCardExpandToc = (
  thumbnail?: DeckCardThumbnailRecord | null,
  card?: PublishedCard | null
): DeckCardTocItem[] => {
  const cardType =
    thumbnail?.type ??
    (typeof card?.pages?.[0]?.type === "string" ? card.pages[0].type : undefined);

  const thumbnailToc = thumbnail?.table_of_contents ?? [];
  const cardToc = normalizeToc(card?.table_of_contents);
  const baseToc = thumbnailToc.length > 0 ? thumbnailToc : cardToc;

  const cardContent = (card?.content ?? card?.pages?.[0]?.content ?? null) as Record<
    string,
    unknown
  > | null;
  const exps =
    readUnknownExps(thumbnail?.content).length > 0
      ? readUnknownExps(thumbnail?.content)
      : readUnknownExps(cardContent).length > 0
        ? readUnknownExps(cardContent)
        : normalizeTocExps(
            cardContent && Array.isArray((cardContent as { exps?: unknown }).exps)
              ? (cardContent as { exps: unknown }).exps
              : undefined
          );

  return mergeDeckCardToc(baseToc, exps, cardType);
};

export const resolveDeckCardExpandSealTime = (
  thumbnail?: DeckCardThumbnailRecord | null,
  card?: PublishedCard | null
): string | number | null => {
  const fromThumbnail = thumbnail?.seal_time;
  if (fromThumbnail != null && fromThumbnail !== "") return fromThumbnail;

  const fromCard = card?.seal_time;
  if (fromCard != null && fromCard !== "") return fromCard;

  const cardContent = (card?.content ?? card?.pages?.[0]?.content ?? null) as Record<
    string,
    unknown
  > | null;
  const fromContent = cardContent?.seal_time;
  if (typeof fromContent === "string" || typeof fromContent === "number") return fromContent;

  const thumbnailContent = thumbnail?.content as Record<string, unknown> | null | undefined;
  const fromThumbnailContent = thumbnailContent?.seal_time;
  if (typeof fromThumbnailContent === "string" || typeof fromThumbnailContent === "number") {
    return fromThumbnailContent;
  }

  return null;
};

export type DeckCardLivingPlanItem = {
  icon: string;
  title: string;
  subtitle: string;
};

const countDeckCardExps = (toc?: DeckCardTocItem[] | null): number => {
  if (!Array.isArray(toc)) return 0;
  return toc.reduce((sum, item) => sum + (item.exps?.length ?? 0), 0);
};

/** Living-plan tiles for season/demo back-face summary. */
export const getDeckCardLivingPlan = (
  record?:
    | (Pick<DeckCardThumbnailRecord, "table_of_contents"> & {
        seal_time?: string | number | null;
      })
    | null,
  labels?: {
    newEpisodesOn?: (date: string) => string;
    episodesAdded?: (count: number) => string;
    sealsOn?: (date: string) => string;
    afterFinalEpisodes?: string;
  }
): DeckCardLivingPlanItem[] => {
  if (!record) return [];

  const items: DeckCardLivingPlanItem[] = [];
  const toc = record.table_of_contents ?? [];
  const allExps = toc.flatMap((item) => item.exps ?? []);
  const episodeCount = allExps.length;
  const now = Date.now();

  const datedExps = allExps
    .map((exp) => ({ exp, ms: getDeckCardExpReleaseMs(exp) }))
    .filter((row): row is { exp: DeckCardTocExp; ms: number } => row.ms != null)
    .sort((a, b) => a.ms - b.ms);

  const upcoming = datedExps.find((row) => row.ms > now);
  const latest = datedExps.length > 0 ? datedExps[datedExps.length - 1] : null;

  if (upcoming) {
    const dateLabel = formatDeckCardDisplayDate(upcoming.ms);
    items.push({
      icon: "📅",
      title: labels?.newEpisodesOn?.(dateLabel) ?? `New episode on ${dateLabel}`,
      subtitle:
        labels?.episodesAdded?.(episodeCount) ??
        `${episodeCount} episode${episodeCount === 1 ? "" : "s"} added.`,
    });
  } else if (latest) {
    const dateLabel = formatDeckCardDisplayDate(latest.ms);
    items.push({
      icon: "📅",
      title: labels?.newEpisodesOn?.(dateLabel) ?? `New episode on ${dateLabel}`,
      subtitle:
        labels?.episodesAdded?.(episodeCount) ??
        `${episodeCount} episode${episodeCount === 1 ? "" : "s"} added.`,
    });
  } else if (episodeCount > 0) {
    items.push({
      icon: "📅",
      title: labels?.episodesAdded?.(episodeCount) ?? `${episodeCount} episodes added.`,
      subtitle: labels?.afterFinalEpisodes ?? "More arrive as the creator publishes.",
    });
  }

  const sealMs = parseSealDeadline(record.seal_time);
  if (sealMs != null) {
    const sealLabel = formatDeckCardDisplayDate(sealMs);
    if (sealLabel) {
      items.push({
        icon: "🔒",
        title: labels?.sealsOn?.(sealLabel) ?? `Seals on ${sealLabel}`,
        subtitle: labels?.afterFinalEpisodes ?? "After final episodes.",
      });
    }
  }

  return items;
};

export const countDeckCardContentPieces = (record?: DeckCardThumbnailRecord | null): number =>
  countDeckCardExps(record?.table_of_contents);
